import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

import { ScriptLoaderService } from '@stair-craft/common/util';

import { ITracker } from './tracker';
import { WINDOW } from './window.token';

export const GTM_CONTAINER_ID: string = 'GTM-KQJ2ML2J';

const GTM_SCRIPT_ID: string = 'sc-gtm';
const GTM_SCRIPT_SRC: string = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;

export type IConsentState = 'granted' | 'denied';
export type IConsentKey = 'ad_storage' | 'ad_user_data' | 'ad_personalization' | 'analytics_storage';

/**
 * Google Tag Manager facade. On browser bootstrap this service:
 *   1. initialises `window.dataLayer = []`;
 *   2. installs a synchronous `window.gtag` stub that proxies into dataLayer;
 *   3. pushes `consent default denied` (GDPR + ePrivacy safe default —
 *      Consent Mode v2 Basic, teams in Advanced via AnalyticsService.updateConsent);
 *   4. pushes `gtm.start` + `gtm.js` so GTM replays our queue after tag load;
 *   5. delegates actual gtm.js loading to ScriptLoaderService with a
 *      triple-trigger strategy;
 *   6. skips everything if the visitor looks like a bot.
 *
 * SSR-safe: bootstrap short-circuits when not in browser, and every push
 * uses optional chaining on dataLayer.
 */
@Injectable({ providedIn: 'root' })
export class GtmService implements ITracker {
    readonly #windowRef: Window = inject(WINDOW);
    readonly #doc: Document = inject(DOCUMENT);
    readonly #platformId: object = inject(PLATFORM_ID);
    readonly #scriptLoader: ScriptLoaderService = inject(ScriptLoaderService);

    #bootstrapped: boolean = false;

    public bootstrap(): void {
        if (this.#bootstrapped) {
            return;
        }
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }
        this.#bootstrapped = true;

        this.#installStubs();

        this.#windowRef.gtag?.('consent', 'default', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
            wait_for_update: 500,
        });

        this.#windowRef.dataLayer?.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

        void this.#scriptLoader.loadDeferred(
            { id: GTM_SCRIPT_ID, src: GTM_SCRIPT_SRC, async: true },
            [{ type: 'user-interaction' }, { type: 'idle', timeoutMs: 4000 }, { type: 'timeout', delayMs: 3000 }],
            true
        );
    }

    public push(payload: Record<string, unknown>): void {
        this.#windowRef.dataLayer?.push(payload);
    }

    public pageView(pagePath: string, pageTitle?: string): void {
        this.push({
            event: 'page_view',
            page_path: pagePath,
            page_title: pageTitle ?? this.#doc.title,
            page_location: this.#windowRef.location.href,
        });
    }

    public event(name: string, params?: Record<string, unknown>): void {
        this.push({ event: name, ...params });
    }

    public setUser(userId: string, props?: Record<string, unknown>): void {
        this.push({ event: 'user_identify', user_id: userId, user_properties: props });
    }

    public consent(update: Partial<Record<IConsentKey, IConsentState>>): void {
        if (typeof this.#windowRef.gtag !== 'function') {
            return;
        }
        this.#windowRef.gtag('consent', 'update', update);
    }

    #installStubs(): void {
        this.#windowRef.dataLayer = this.#windowRef.dataLayer ?? [];
        if (typeof this.#windowRef.gtag !== 'function') {
            const layer: (Record<string, unknown> | IArguments)[] = this.#windowRef.dataLayer;
            this.#windowRef.gtag = function gtag(...args: unknown[]): void {
                layer.push(args as unknown as IArguments);
            };
        }
    }
}
